// Recruiting — typed client for GET /api/v1/recruiting/* (People → Recruiting
// tab, owner preview: lib/ownerPreview.ts). Code against this contract
// exactly; the server is being built alongside this file.
//
// Kept across visits in two halves (lib/dataVersions.ts, owner 2026-09-25:
// "all past records are static and only those active get updates"): the
// Archive pages and the cards of people with an outcome stay saved until one
// of them changes; the summary, the pipeline pages and the cards of people
// in play follow the live fingerprint, which moves on any write and at
// least once a day. The key shapes below are what those groups match on.
import { authedGet } from './apiClient';
import type { LocationCode } from './locations';

export const RECRUITING_STAGES = [
  'LEAD', 'TO_REVIEW', 'PHONE_SCREEN', 'INTERVIEW', 'PRACTICAL', 'OFFER', 'HOLD', 'HIRED',
] as const;
export type RecruitingStage = (typeof RECRUITING_STAGES)[number];

export const STAGE_LABEL: Record<RecruitingStage, string> = {
  LEAD: 'Lead',
  TO_REVIEW: 'To review',
  PHONE_SCREEN: 'Phone screen',
  INTERVIEW: 'Interview',
  PRACTICAL: 'Practical',
  OFFER: 'Offer',
  HOLD: 'Hold',
  HIRED: 'Hired',
};

export interface RecruitingClinicSummary {
  stages: Record<RecruitingStage, number>;
  inPlay: number;
  quiet7: number;
  newToday: number;
  doNotContact: number;
  archive: number;
  lmtHeadcount: number | null;
}

export interface RecruitingAllSummary extends RecruitingClinicSummary {
  noClinic: number;
}

export interface RecruitingSummary {
  clinics: Record<LocationCode, RecruitingClinicSummary>;
  all: RecruitingAllSummary;
  generatedAt: string;
}

export const RECRUITING_SUMMARY_KEY = 'recruiting-summary';

export function fetchRecruitingSummary(): Promise<RecruitingSummary> {
  return authedGet<RecruitingSummary>('/api/v1/recruiting/summary');
}

/** One application that arrived by itself (the Indeed intake) and nobody has
 * acted on yet — GET /api/v1/recruiting/new-applications (src/recruiting.js
 * listNewApplications; owner, 2026-09-29). */
export interface NewApplication {
  id: number;
  candidateId: number;
  locationCode: LocationCode | null;
  position: string | null;
  source: string | null;
  appliedAt: string;
  receivedAt: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  stage: RecruitingStage;
  doNotContact: boolean;
  resumeUrl: string | null;
}

export interface NewApplicationsResponse {
  applications: NewApplication[];
  truncated: boolean;
  generatedAt: string;
}

export const RECRUITING_NEW_APPLICATIONS_KEY = 'recruiting-new-applications';

export function fetchNewApplications(): Promise<NewApplicationsResponse> {
  return authedGet<NewApplicationsResponse>('/api/v1/recruiting/new-applications');
}

/** The four positions as a person reads them (the server's twin is
 * src/recruiting_notify.js POSITION_WORDS). */
export const POSITION_WORDS: Record<string, string> = {
  LMT: 'Massage Therapist',
  EST: 'Esthetician',
  FRONT_DESK: 'Front Desk',
  MANAGER: 'Manager',
};

export const RECRUITING_FLAGS = ['DO_NOT_CONTACT', 'OLD_EMPLOYEE', 'CURRENT_EMPLOYEE', 'NO_RESUME', 'QUIET_7', 'APPLIED_BEFORE', 'IN_PROCESS'] as const;
export type RecruitingFlag = (typeof RECRUITING_FLAGS)[number];

// docs/RECRUITING-PLAN.md, "What the board is": "Do not contact, Old
// employee, Resume missing, Quiet 7+ days, Applied before."
export const FLAG_LABEL: Record<RecruitingFlag, string> = {
  DO_NOT_CONTACT: 'Do not contact',
  OLD_EMPLOYEE: 'Old employee',
  CURRENT_EMPLOYEE: 'Current employee',
  NO_RESUME: 'Resume missing',
  QUIET_7: 'Quiet 7+',
  APPLIED_BEFORE: 'Applied before',
  // Owner, 2026-10-06: phone screen through offer, no outcome (server isInProcess).
  IN_PROCESS: 'In process',
};

export interface CandidateRow {
  id: number;
  firstName: string;
  lastName: string;
  middleName: string | null;
  position: string | null;
  stage: RecruitingStage;
  stageSince: string | null;
  outcome: string | null;
  outcomeAt: string | null;
  outcomeReason: string | null;
  doNotContact: boolean;
  dncReason: string | null;
  homeLocationCode: string | null;
  hiringManager: string | null;
  email: string | null;
  emailRelay: string | null;
  phone: string | null;
  licenseNumber: string | null;
  licenseStatus: string | null;
  /** Owner 2026-09-28: set only by a manager's own "License checked on DOH" record. */
  licenseExpires?: string | null;
  licenseCheckedAt?: string | null;
  licenseCheckedBy?: string | null;
  licenseCheckNote?: string | null;
  source: string | null;
  receivedAt: string | null;
  lastTouchAt: string | null;
  employeeId: number | null;
  alsoApplied: string[];
  applications: number;
  hasFile: boolean;
  /** "The resume" (owner, 2026-10-11): the file someone marked current, else
   * the newest RESUME by a trusted received/attached date (server
   * src/recruiting.js resumeOrderSql). Null when there is none. */
  resumeFileId?: number | null;
  resumeName?: string | null;
  resumeUrl?: string | null;
  resumeIsCurrent?: boolean;
  flags: RecruitingFlag[];
}

export interface CandidatesResponse {
  rows: CandidateRow[];
  total: number;
}

export type RecruitingLocationParam = LocationCode | 'ALL' | 'ARCHIVE';

export interface CandidatesQuery {
  location: RecruitingLocationParam;
  stage?: RecruitingStage | null;
  /** "New today" (2026-09-30): the same people the New today number counts. */
  newToday?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export const RECRUITING_CANDIDATES_DEFAULT_LIMIT = 50;

/** One cache key per distinct query. A search box changing this on every
 * keystroke is exactly why the caller debounces before building it — see
 * RecruitingBoard.tsx. */
export function recruitingCandidatesKey(query: CandidatesQuery): string {
  return [
    'recruiting-candidates',
    query.location,
    query.stage ?? '',
    (query.q ?? '').trim().toLowerCase(),
    query.limit ?? RECRUITING_CANDIDATES_DEFAULT_LIMIT,
    query.offset ?? 0,
    ...(query.newToday ? ['new-today'] : []),
  ].join(':');
}

export function fetchRecruitingCandidates(query: CandidatesQuery): Promise<CandidatesResponse> {
  const params = new URLSearchParams();
  params.set('location', query.location);
  if (query.stage) params.set('stage', query.stage);
  if (query.newToday) params.set('new', 'today');
  const q = (query.q ?? '').trim();
  if (q) params.set('q', q);
  params.set('limit', String(query.limit ?? RECRUITING_CANDIDATES_DEFAULT_LIMIT));
  params.set('offset', String(query.offset ?? 0));
  return authedGet<CandidatesResponse>(`/api/v1/recruiting/candidates?${params.toString()}`);
}

export interface CandidateApplication {
  id: number;
  locationCode: string | null;
  appliedArea: string | null;
  appliedAt: string | null;
  position: string | null;
  source: string | null;
  externalRef: string | null;
  note: string | null;
}

export interface CandidateFile {
  id: number;
  kind: string;
  name: string;
  url: string | null;
  driveFileId: string | null;
  sha256: string | null;
  sourcePath: string | null;
  fileDate: string | null;
  addedAt: string | null;
  /** The received/attached date the resume order uses; null when it cannot
   * be trusted (a file moved from Monday carries the move date, 07/29/2026). */
  resumeDate?: string | null;
  /** True when file_date is the Monday move date, not the resume's. */
  mondayMigrationDate?: boolean;
  /** True for the file someone marked as the current resume. */
  isCurrent?: boolean;
}

export interface CandidateLogEntry {
  id: number;
  kind: string;
  body: string | null;
  author: string | null;
  occurredAt: string;
  meta?: Record<string, unknown> | null;
}

export interface CandidateIdentity {
  kind: string;
  value: string;
}

/** docs/RECRUITING-PLAN.md rule 8's merge: candidates sharing an email,
 * phone, license number or resume hash — compared on `candidate`'s own
 * columns and `candidate_file.sha256` (never `candidate_identity`, which is
 * globally unique per value and so can never be shared by two DIFFERENT
 * candidate rows — src/recruiting.js's own header comment on this). */
export interface PossibleDuplicate {
  id: number;
  firstName: string;
  lastName: string;
  sharedIdentity: { kind: 'EMAIL' | 'PHONE' | 'LICENSE' | 'RESUME_SHA'; value: string | null };
}

export interface CandidateDetail {
  candidate: CandidateRow & {
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    summary: string | null;
    createdAt: string | null;
    referredByEmployeeId: number | null;
    referredByName: string | null;
    /** The file marked as current, while it is still on file; else null. */
    currentResumeFileId?: number | null;
  };
  applications: CandidateApplication[];
  files: CandidateFile[];
  log: CandidateLogEntry[];
  identities: CandidateIdentity[];
  possibleDuplicates: PossibleDuplicate[];
}

/** A candidate's card. `archived` names the side of the Archive line the
 * card was opened from (the Archive tab, or a pipeline tab), so
 * lib/dataVersions.ts keeps a past person's card between visits and lets an
 * in-play person's card follow the live pipeline. Without it (Manager
 * Entry's prefill, a card opened straight after Add candidate) the key is
 * plain: fetched once per session, never saved — exactly as before. */
export function recruitingCandidateKey(id: number, archived?: boolean | null): string {
  if (archived === true) return `recruiting-candidate:past:${id}`;
  if (archived === false) return `recruiting-candidate:live:${id}`;
  return `recruiting-candidate:${id}`;
}

export function fetchRecruitingCandidate(id: number): Promise<CandidateDetail> {
  return authedGet<CandidateDetail>(`/api/v1/recruiting/candidates/${id}`);
}

/** The log's `kind` values named in docs/RECRUITING-PLAN.md / the build spec.
 * A kind this map does not recognise renders as its own raw text rather than
 * disappearing (same discipline as People.tsx's employmentShort — an
 * unexpected value is worth seeing, not worth hiding). */
export const LOG_KIND_LABEL: Record<string, string> = {
  NOTE: 'Note',
  CALL: 'Call',
  TEXT: 'Text',
  EMAIL_IN: 'Email in',
  EMAIL_OUT: 'Email out',
  STAGE: 'Stage',
  OUTCOME: 'Outcome',
  DO_NOT_CONTACT: 'Do not contact',
  DNC: 'Do not contact',
  SCORECARD: 'Scorecard',
  HIRE: 'Hire',
  SYSTEM: 'System',
  MIGRATED: 'Migrated',
};

export function logKindLabel(kind: string): string {
  return LOG_KIND_LABEL[kind] ?? kind;
}

// --- templates + email (docs/RECRUITING-PLAN.md item 5) --------------------

export interface RecruitingTemplate {
  id: number;
  scope: 'COMPANY' | 'MINE';
  ownerEmail: string | null;
  name: string;
  subject: string;
  body: string;
  createdAt: string | null;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const RECRUITING_TEMPLATES_KEY = 'recruiting-templates';

export function fetchRecruitingTemplates(): Promise<{ templates: RecruitingTemplate[] }> {
  return authedGet<{ templates: RecruitingTemplate[] }>('/api/v1/recruiting/templates');
}

export interface CandidateEmailEntry {
  id: number;
  candidateId: number;
  direction: 'OUT' | 'IN';
  gmailMessageId: string;
  gmailThreadId: string | null;
  mailbox: string;
  fromAddress: string;
  toAddresses: string;
  subject: string | null;
  bodyText: string | null;
  sentAt: string;
  loggedBy: string;
  loggedAt: string;
}

/**
 * MM/DD/YYYY from a bare 'YYYY-MM-DD' (or a longer timestamp — only the
 * first 10 characters are read) — string splitting, never a `Date` object,
 * the same no-timezone discipline civilDate.ts's own arithmetic exists for
 * (INV-8: no timezone may move a day). Recruiting keeps its own copy rather
 * than reaching into serviceCompliance.ts's isoToMd, which this build does
 * not otherwise touch.
 */
export function formatCivilDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
}

// --- the rest of nvRecruiter (docs/RECRUITING-PLAN.md; owner, 2026-09-27) --

export interface RecruitingNumbers {
  from: string;
  to: string;
  clinic: string | null;
  hiresBySource: { source: string; count: number }[];
  applicationsBySource: { source: string; count: number }[];
  timeInStage: { stage: string; transitions: number; medianDays: number | null }[];
  outcomes: { outcome: string; reason: string | null; count: number }[];
  hires: number;
  terminations: number;
  generatedAt: string;
}

export interface NumbersQuery {
  from: string;
  to: string;
  clinic?: string | null;
}

export function recruitingNumbersKey(query: NumbersQuery): string {
  return ['recruiting-numbers', query.from, query.to, query.clinic ?? ''].join(':');
}

export function fetchRecruitingNumbers(query: NumbersQuery): Promise<RecruitingNumbers> {
  const params = new URLSearchParams();
  params.set('from', query.from);
  params.set('to', query.to);
  if (query.clinic) params.set('clinic', query.clinic);
  return authedGet(`/api/v1/recruiting/numbers?${params.toString()}`);
}

// --- Same-name review (owner/director; owner, 2026-10-11) ------------------

/** One record in a same-name group, side by side (src/recruiting_cleanup.js). */
export interface SameNameMember {
  id: number;
  firstName: string;
  middleName: string | null;
  lastName: string;
  homeLocationCode: string | null;
  email: string | null;
  emailRelay: string | null;
  phone: string | null;
  phoneAlt: string | null;
  licenseNumber: string | null;
  position: string | null;
  stage: RecruitingStage;
  outcome: string | null;
  doNotContact: boolean;
  source: string | null;
  receivedAt: string | null;
  lastTouchAt: string | null;
  createdAt: string | null;
  employeeId: number | null;
  applications: number;
  resumes: number;
  files: number;
}

export interface SameNameGroup {
  /** first|last, lower-cased, every space removed — an exact key. */
  nameKey: string;
  candidateIds: number[];
  members: SameNameMember[];
  /** Emails / phones / licenses / resume files two or more members share. */
  sharedIdentities: { kind: 'EMAIL' | 'PHONE' | 'LICENSE' | 'RESUME_SHA'; value: string; candidateIds: number[] }[];
}

export interface SameNameResponse {
  groups: SameNameGroup[];
  totalGroups: number;
  totalRecords: number;
  dismissedGroups: number;
  truncated: boolean;
  generatedAt: string;
}

export const RECRUITING_SAME_NAME_KEY = 'recruiting-same-name';

export function fetchSameNameGroups(): Promise<SameNameResponse> {
  return authedGet<SameNameResponse>('/api/v1/recruiting/same-name');
}
